import type { ComponentType, ReactElement } from 'react';

import { DataTestIds } from '@repo/frontend-shared';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSPrimitiveButton } from '@repo/sds/SDSPrimitiveButton';
import { VerticalLogoWithTextIcon } from '@repo/ui/assets/icons/brandIcons/VerticalLogoWithTextIcon';
import { HeaderMobileMenu } from '@repo/ui/fixtures/header/components/HeaderMobileMenu/HeaderMobileMenu';

import type { LinkButton } from '../../types/menu-items';
import { HeaderDesktopMenu } from '../HeaderDesktopMenu/HeaderDesktopMenu';

import './Header.scss';

interface HeaderProps {
  navLinks?: LinkButton[];
  logoHref: string;
  preNavLinkDesktopContent?: ReactElement;
  postNavLinkDesktopContent?: ReactElement;
  postNavLinkMobileContent?: ReactElement;
  /**
   * Admin wordmark. Passed in by admin callers so other apps never bundle it.
   * When set, the header uses the admin logo styling and label.
   */
  adminLogoIcon?: ComponentType;
  dataTestId?: string;
}

const EMPTY_NAV_LINKS: LinkButton[] = [];

/**
 * @description SSR Optimized component
 */
const HeaderLogo = ({ adminLogoIcon, logoHref }: { adminLogoIcon?: ComponentType; logoHref?: string }) => {
  const className = adminLogoIcon ? 'Header__admin-logo' : 'Header__logo';
  const Icon = adminLogoIcon ?? VerticalLogoWithTextIcon;
  const label = adminLogoIcon ? 'StartHub Admin Dashboard' : 'StartHub Dashboard';

  return (
    <SDSFlexbox className={className}>
      <SDSPrimitiveButton dataTestId={DataTestIds.COMMON.HEADER_LOGO} href={logoHref} ariaLabel={label}>
        <Icon />
      </SDSPrimitiveButton>
    </SDSFlexbox>
  );
};

/**
 * Filters nav links based on device visibility.
 * - Desktop: excludes 'mobile-only' items
 * - Mobile: excludes 'desktop-only' items
 */
const filterByVisibility = (links: LinkButton[], device: 'desktop' | 'mobile'): LinkButton[] => {
  return links.filter(link => {
    const visibility = link.visibility ?? 'all';
    if (device === 'desktop') {
      return visibility !== 'mobile-only';
    }
    return visibility !== 'desktop-only';
  });
};

export function Header({
  navLinks = EMPTY_NAV_LINKS,
  logoHref,
  preNavLinkDesktopContent,
  postNavLinkDesktopContent,
  postNavLinkMobileContent,
  adminLogoIcon,
  dataTestId,
}: HeaderProps) {
  const desktopLinks = filterByVisibility(navLinks, 'desktop');
  const mobileLinks = filterByVisibility(navLinks, 'mobile');

  const shouldRenderMobile = mobileLinks.length > 0 || postNavLinkMobileContent;
  const shouldRenderDesktop = desktopLinks.length > 0 || preNavLinkDesktopContent || postNavLinkDesktopContent;

  return (
    <header className="Header" data-testid={dataTestId}>
      <HeaderLogo adminLogoIcon={adminLogoIcon} logoHref={logoHref} />
      {shouldRenderDesktop && (
        <HeaderDesktopMenu
          navLinks={desktopLinks}
          preNavLinkContent={preNavLinkDesktopContent}
          postNavLinkContent={postNavLinkDesktopContent}
        />
      )}
      {shouldRenderMobile && <HeaderMobileMenu navLinks={mobileLinks} postNavLinkContent={postNavLinkMobileContent} />}
    </header>
  );
}
